import {Platform} from './types'

/**
 * Mixpanel property naming the client UI the user is in — not the OS
 * (Mixpanel's `$os` covers that) and not the codebase that fired the event.
 * Registered as a device super-property by the web app, next-frontend, and
 * Android; stamped per-event by the server (`services/api`'s
 * `trackServerEvent`) when the originating client is derivable. See
 * docs/analytics.md, "`Notability Platform` super-property".
 */
export const NOTABILITY_PLATFORM_PROPERTY = 'Notability Platform'

/**
 * Android and iOS spell these values in their own codebases; a change here
 * must ship in lockstep there or cross-platform reports fork.
 */
export const notabilityPlatformValues = ['Web', 'iOS', 'Android', 'Desktop Mac', 'Desktop Windows'] as const

export type NotabilityPlatform = (typeof notabilityPlatformValues)[number]

/**
 * Maps the `Platform` request enum (or the same value round-tripped through
 * Stripe metadata as a string) to the Mixpanel vocabulary. Returns undefined
 * for MISSING or unrecognized input so the caller omits the property —
 * an absent property is recoverable in Mixpanel, a wrong value is not.
 * The desktop shell sends `client-platform: WEB`, so a WEB result may mean
 * either a browser or the shell; `notabilityPlatformFromStripeMetadata` below
 * refines it when checkout recorded the shell OS.
 */
export function notabilityPlatformFromClientPlatform(platform: string | undefined): NotabilityPlatform | undefined {
  switch (platform) {
    case Platform.WEB:
      return 'Web'
    case Platform.IOS:
      return 'iOS'
    case Platform.ANDROID:
      return 'Android'
    default:
      return undefined
  }
}

/**
 * Request header that names the desktop shell's host OS alongside `client-platform: WEB`,
 * sent from inside the shell or from a page opened by a shell subscribe link. Any browser
 * holding that link can send it, so it is for analytics only, never for trust or behavior.
 */
export const DESKTOP_SHELL_OS_HEADER = 'client-desktop-shell-os'

/**
 * Query param the shell's subscribe links carry, because the packaged shell opens
 * the subscribe page in the OS browser, where there is no preload bridge to read.
 */
export const DESKTOP_SHELL_OS_QUERY_PARAM = 'desktopShellOs'

export const desktopShellOSValues = ['mac', 'windows'] as const

export type DesktopShellOS = (typeof desktopShellOSValues)[number]

export function parseDesktopShellOS(value: unknown): DesktopShellOS | undefined {
  return desktopShellOSValues.find((candidate) => candidate === value)
}

/**
 * Derives the Mixpanel value from the two facts checkout creation copies into
 * Stripe metadata. A shell OS only refines a WEB checkout; on any other platform
 * it is ignored because no other client runs inside the shell.
 */
export function notabilityPlatformFromStripeMetadata(
  metadata: {platform?: string; desktopShellOs?: string} | null | undefined
): NotabilityPlatform | undefined {
  const desktopShellOS = parseDesktopShellOS(metadata?.desktopShellOs)
  if (metadata?.platform === Platform.WEB && desktopShellOS !== undefined) {
    return desktopPlatformForShellOS(desktopShellOS)
  }
  return notabilityPlatformFromClientPlatform(metadata?.platform)
}

export type DesktopPlatform = Extract<NotabilityPlatform, 'Desktop Mac' | 'Desktop Windows'>

export function desktopPlatformForShellOS(shellOS: DesktopShellOS): DesktopPlatform {
  switch (shellOS) {
    case 'mac':
      return 'Desktop Mac'
    case 'windows':
      return 'Desktop Windows'
  }
}
